import { type FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Column, Grid, SkeletonPlaceholder, SkeletonText } from '@carbon/react';
import type { AnimationItem, LottiePlayer } from 'lottie-web/build/player/lottie_svg';

import { PAUSA_NO_NOME, QUADRO_DO_NOME, SAIDA_DA_ESPERA, pintarMarca } from '@maestra/core/constants/marcaLottie';

import abertura from '../../assets/lottie/maestra-splash.json';
import styles from './Carregando.module.scss';

// A ESPERA — duas, e só duas (pedido do Lucas, 27/09/2026: eram três, e duas do desenho antigo):
//
// 1. `CarregandoApp`, a abertura do app (a sessão, o consentimento, o primeiro pedaço do código, as
//    telas cheias fora da casca): a animação da marca, a MESMA do app nativo e a única (a colorida
//    em que o nome aparece depois; a pintura mora no núcleo, `marcaLottie`). Ela toca uma vez e
//    para no último quadro, com o símbolo e o nome.
// 2. `CarregandoPagina`, dentro do app: a casca fica, e o conteúdo é o esqueleto de uma página (o
//    título, as linhas e um bloco). É o que se vê ao entrar num módulo, ao trocar de tela e na
//    checagem dos porteiros de rota. Dentro de uma tela, o que carrega mostra o esqueleto dele
//    (`DataTableSkeleton`, `SkeletonText`), e uma ação em curso usa o `InlineLoading` do botão.
//
// Nada de roda girando: é o sinal de espera de qualquer aplicativo.

export const CarregandoApp: FC<{ descricao?: string; aoTerminar?: () => void }> = ({ descricao = 'Carregando', aoTerminar }) => {
  const palco = useRef<HTMLSpanElement>(null);
  // Por ref: trocar a função a cada render não pode recomeçar a animação.
  const fim = useRef(aoTerminar);
  fim.current = aoTerminar;

  useEffect(() => {
    const el = palco.current;
    if (!el) return undefined;
    const semMovimento = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let player: LottiePlayer | undefined;
    let atual: AnimationItem | undefined;
    let vivo = true;
    let pausa: ReturnType<typeof setTimeout> | undefined;
    const terminar = (espera: number) => {
      clearTimeout(pausa);
      pausa = setTimeout(() => { if (vivo) fim.current?.(); }, espera);
    };

    const comecar = () => {
      if (!vivo || !player) return;
      atual?.destroy();
      const animacao = player.loadAnimation({
        container: el,
        renderer: 'svg',
        loop: false,
        autoplay: !semMovimento,
        animationData: pintarMarca(abertura, getComputedStyle(el).color),
        rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
      });
      atual = animacao;
      // Quem pediu menos movimento vê a marca inteira e parada: o último quadro, direto.
      if (semMovimento) {
        animacao.addEventListener('DOMLoaded', () => {
          animacao.goToAndStop(QUADRO_DO_NOME, true);
          terminar(PAUSA_NO_NOME * 2);
        });
      } else {
        // Só até o nome assentar: o resto do arquivo é imagem parada, que lia como tela travada.
        // ⚠️ A trava: o `goToAndStop` dispara outro `enterFrame` antes de pausar, e sem ela o
        // ouvinte entrava em si mesmo até estourar a pilha (a espera nunca saía).
        let parou = false;
        animacao.addEventListener('enterFrame', () => {
          if (parou || animacao.currentFrame < QUADRO_DO_NOME) return;
          parou = true;
          animacao.goToAndStop(QUADRO_DO_NOME, true);
          terminar(PAUSA_NO_NOME);
        });
      }
    };

    // O player chega sob demanda (fora do primeiro pedaço do código), e no build só de SVG: o
    // completo traz o canvas, que a espera não usa. É a única biblioteca de fora na UI nova
    // (`uiCarbon.test.ts`, decisão do Lucas em 27/09/2026).
    import('lottie-web/build/player/lottie_svg').then(({ default: lottie }) => {
      player = lottie;
      comecar();
    }).catch(() => undefined);

    // A tinta é lida na hora de tocar: se o tema trocar com a espera na tela, recomeça com a nova.
    const trocaDeTema = new MutationObserver(comecar);
    trocaDeTema.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => { vivo = false; clearTimeout(pausa); trocaDeTema.disconnect(); atual?.destroy(); };
  }, []);

  return (
    <div className={styles.app} role='status' aria-live='polite' data-ui-carbon=''>
      <span ref={palco} className={styles.palco} aria-hidden />
      <span className={styles.escondido}>{descricao}</span>
    </div>
  );
};

/**
 * A ABERTURA DA PÁGINA: a espera da marca por cima de tudo, do primeiro quadro até o nome
 * (`PAUSA_NO_NOME`), mesmo que a sessão, a loja e o primeiro pedaço do código fiquem prontos
 * antes. Depois sai, e as esperas seguintes são as de sempre. Montada uma vez, na raiz do app.
 */
export const AberturaDaMarca: FC = () => {
  // Na vitrine (revisão e captura das telas) a abertura não entra: ela cobriria a tela revisada.
  const [fase, setFase] = useState<'tocando' | 'saindo' | 'fora'>(
    () => (window.location.pathname.startsWith('/__vitrine') ? 'fora' : 'tocando'));
  // Enquanto a abertura toca, o <html> leva `data-abertura`: a entrada das páginas (o topo da
  // landing, o desenho dele) espera com a animação pausada, e começa quando a marca sai. Sem isto,
  // a entrada inteira acontecia por baixo da marca, e a página aparecia pronta, dura.
  useLayoutEffect(() => {
    const raiz = document.documentElement;
    if (fase === 'tocando') raiz.dataset.abertura = 'tocando';
    else delete raiz.dataset.abertura;
    return () => { delete raiz.dataset.abertura; };
  }, [fase]);
  // Sai num fade (o `moderate-02` do Carbon), e só então desmonta.
  useEffect(() => {
    if (fase !== 'saindo') return undefined;
    const fim = setTimeout(() => setFase('fora'), SAIDA_DA_ESPERA);
    return () => clearTimeout(fim);
  }, [fase]);
  if (fase === 'fora') return null;
  return (
    <div className={[styles.abertura, fase === 'saindo' && styles.saindo].filter(Boolean).join(' ')}>
      <CarregandoApp aoTerminar={() => setFase('saindo')} />
    </div>
  );
};

/**
 * O miolo do esqueleto (o título, as linhas e o bloco), para uma tela que já está com a moldura na
 * tela (a trilha do show, por exemplo) e só o conteúdo ainda chega. `compacto`: um pedaço da tela
 * (sem o bloco grande).
 */
export const EsqueletoDaPagina: FC<{ descricao?: string; compacto?: boolean }> = ({ descricao = 'Carregando', compacto }) => (
  <div role='status' aria-live='polite' aria-busy='true'>
    <span className={styles.escondido}>{descricao}</span>
    <SkeletonText heading width='40%' />
    <SkeletonText paragraph lineCount={3} />
    {!compacto && <SkeletonPlaceholder className={styles.bloco} />}
  </div>
);

export const CarregandoPagina: FC<{ descricao?: string; compacto?: boolean }> = ({ descricao, compacto }) => (
  <Grid className={styles.pagina}>
    <Column sm={4} md={8} lg={16}>
      <EsqueletoDaPagina descricao={descricao} compacto={compacto} />
    </Column>
  </Grid>
);
